import { VARIABLES_BY_KEY, type VariableKey } from '../lib/census-variables';
import { formatShort } from '../lib/format';
import { useI18n } from '../lib/i18n';

export type LegendMode = 'continuous' | 'revista';

// CSS custom properties for UI chrome (legend swatches, etc.) where
// the browser resolves them at paint time.
export const DATA_STOPS = [
  'var(--data-0)',
  'var(--data-1)',
  'var(--data-2)',
  'var(--data-3)',
  'var(--data-4)',
  'var(--data-5)',
];

// Raw hex values — MapLibre paint expressions can't consume CSS vars,
// so the map colors have to be resolved ahead of time. Keep in sync
// with src/tokens.css.
export const DATA_STOPS_HEX = [
  '#f5f1e8',
  '#d8d3c7',
  '#9e9a90',
  '#5c5954',
  '#2a2825',
  '#000000',
];

// Canonical bin breaks from the printed Revista étnica map. Percentages
// stored as proportions (0..1) to match our data file.
export const REVISTA_BREAKS = [0, 0.0515, 0.0969, 0.1555, 0.2242, 0.7063];

export function Legend({
  variable,
  mode,
  onMode,
  min,
  max,
}: {
  variable: VariableKey;
  mode: LegendMode;
  onMode: (m: LegendMode) => void;
  min: number | null;
  max: number | null;
}) {
  const { t, lang } = useI18n();
  const meta = VARIABLES_BY_KEY[variable];
  const canToggleRevista = variable === 'pct_black_alone';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="display text-xs uppercase tracking-[0.2em] text-an-muted">
          {t('app.legend.title')}
        </h3>
        {canToggleRevista && (
          <div className="flex gap-0 overflow-hidden rounded-sm border border-an-rule text-[10px]">
            <button
              className={
                'display px-2 py-1 uppercase tracking-widest ' +
                (mode === 'continuous' ? 'bg-an-black text-an-white' : 'bg-an-white text-an-ink')
              }
              onClick={() => onMode('continuous')}
            >
              {t('app.legend.continuous')}
            </button>
            <button
              className={
                'display border-l border-an-rule px-2 py-1 uppercase tracking-widest ' +
                (mode === 'revista' ? 'bg-an-black text-an-white' : 'bg-an-white text-an-ink')
              }
              onClick={() => onMode('revista')}
            >
              {t('app.legend.revista')}
            </button>
          </div>
        )}
      </div>

      {mode === 'revista' && canToggleRevista ? (
        <div>
          <div className="flex h-4 w-full overflow-hidden border border-an-rule">
            {REVISTA_BREAKS.slice(0, -1).map((_, i) => (
              <div key={i} className="flex-1" style={{ backgroundColor: DATA_STOPS[i + 1] }} />
            ))}
          </div>
          <div className="mt-1 flex justify-between font-body text-[10px] text-an-muted">
            {REVISTA_BREAKS.map((b, i) => (
              <span key={i}>{(b * 100).toFixed(1)}%</span>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div
            className="h-4 w-full border border-an-rule"
            style={{
              background: `linear-gradient(to right, ${DATA_STOPS[0]}, ${DATA_STOPS[5]})`,
            }}
          />
          <div className="mt-1 flex justify-between font-body text-[10px] text-an-muted">
            <span>{formatShort(min, meta.format, lang)}</span>
            <span>{formatShort(max, meta.format, lang)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
